import { FC, useContext, useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useDebounce } from "../../hooks/useDebounce";
import useListFilters from "../../hooks/useListFilters";
import { useI18n } from "../../hooks/useTranslations";
import { getPurchaseTypes } from "../../http/carPurchases/types";
import { useAdminCarsApi } from "../../http/cars/api";
import {
  CarNoCarDepency,
  getCarStateColor,
  getCategories,
  getFuelTypes,
  getStatus,
} from "../../http/cars/types";
import { useSubsidiariesApi } from "../../http/subsidiaries/api";
import { FilterParam, NestedPick, PaginationQueryWithFields } from "../../types";
import {
  downloadFileFromData,
  formatBoolean,
  formatDateNumeric,
  formatNumber,
  isWithinLast48h,
} from "../../types/utils";
import SvgAdd from "../icons/Add";
import Button from "../ui/Button";
import BooleanColumnFilter from "../ui/columnFilters/BooleanColumnFilter";
import DateColumnFilter from "../ui/columnFilters/DateColumnFilter";
import NumberColumnFilter from "../ui/columnFilters/NumberColumnFilter";
import TextColumnFilter from "../ui/columnFilters/TextColumnFilter";
import Header from "../ui/Header";
import HistoryDialog from "../ui/HistoryDialog";
import Panel from "../ui/Panel";
import Table, { TableViews } from "../ui/Table";
import Textarea from "../ui/Textarea";

import NotificationDispatch, { showErrorNotification } from "../../context/notificationContext";
import { useUserSettings } from "../../context/userSettingsContext";
import useDataInitializer from "../../hooks/useDataInitializer";
import { getAssignmentTypes } from "../../http/otherCarAssignment/types";
import EditCarDriverDialog from "../carDrivers/EditCarDriverDialog";
import SvgArticle from "../icons/Article";
import SvgCancel from "../icons/Cancel";
import SvgCheckCircle from "../icons/CheckCircle";
import EditManufacturerDialog from "../manufacturers/EditManufacturerDialog";
import EditOtherCarAssignmentDialog from "../otherCarAssignment/EditOtherCarAssignmentDialog";
import { Check } from "../ui/Check";
import CustomLink from "../ui/CustomLink";
import ExportButton from "../ui/ExportButton";
import { Action, getDeleteAction, getHistoryAction } from "../ui/ListItemMenu";
import NewItemIcon from "../ui/NewItemIcon";
import { State } from "../ui/State";
import VersionDialog from "../ui/VersionDialog";
import CarStats from "./CarStats";
import DeleteCarDialog from "./DeleteCarDialog";

const CAR_FIELDS = [
  "id",
  "createdAt",
  "currentPlateNumber",
  "lastPlateNumber",
  "currentAssignment.carDriver.id",
  "currentAssignment.carDriver.driver.id",
  "currentAssignment.carDriver.driver.fullName",
  "currentAssignment.carDriver.driver.currentCostCenter",
  "currentAssignment.carDriver.startDate",
  "currentAssignment.otherCarAssignment.id",
  "currentAssignment.otherCarAssignment.type",
  "currentAssignment.otherCarAssignment.costCenter",
  "currentAssignment.otherCarAssignment.startDate",
  "category",
  "registrationDate",
  "manufacturer.id",
  "manufacturer.name",
  "model",
  "hp",
  "fuelType",
  "vehicleIdentificationNumber",
  "lastMileage",
  "isPrivateCar",
  "status",
  "carSubsidiary.subsidiary.id",
  "carSubsidiary.subsidiary.name",
  "carSubsidiary.subsidiary.semiramisOrganizationCode",
  "purchaseType",
  "versionNumber",
  "purchaseDate",
  "soldDate",
] as const;
type CarField = (typeof CAR_FIELDS)[number];
type CarSearch = NestedPick<CarNoCarDepency, CarField>;

const SUBSIDIARIES_FIELDS = ["id", "name"] as const;

export type Statistic = {
  number: number;
  title: string;
  color: string;
};

const Cars: FC = () => {
  const i18n = useI18n();
  const userSettings = useUserSettings();
  const carsApi = useAdminCarsApi();
  const subsidiariesApi = useSubsidiariesApi();
  const { subsidiaryId } = useParams<{ subsidiaryId: string }>();
  // const preFilters: FilterParam[] = [{ fieldname: "car.status", values: ["active"] }];
  const preFilters: FilterParam[] = [];
  if (subsidiaryId) {
    preFilters.push({ fieldname: "car.subsidiary.id", values: [subsidiaryId] });
  }

  const filters = useListFilters({
    isSearchable: true,
    filters: preFilters.length > 0 ? preFilters : undefined,
  });

  const debouncedValue = useDebounce(filters.state.q);
  const aborter = useRef(new AbortController());
  const [itemToDelete, setItemToDelete] = useState<CarSearch>();
  const navigate = useNavigate();
  const dispatch = useContext(NotificationDispatch);

  const [itemToShowVersions, setItemToShowVersions] = useState<CarSearch>();
  const [carDriverIdToEdit, setCarDriverIdToEdit] = useState<string>();
  const [otherAssignmentIdToEdit, setOtherAssignmentIdToEdit] = useState<string>();
  const [manufacturerIdToEdit, setManufacturerIdToEdit] = useState<string>();

  const navToEdit = (item: CarSearch) => {
    navigate(`/admin/cars/${item.id}`);
  };

  const query: PaginationQueryWithFields<CarNoCarDepency> = {
    ...filters.state,
    q: debouncedValue,
    fields: CAR_FIELDS,
  };

  const result = carsApi.useCars(query, aborter.current.signal);
  const [cars] = useDataInitializer(result.data, result.error);

  const subsidiaries = subsidiariesApi.useAdminSubsidiaries(
    { limit: 100, fields: SUBSIDIARIES_FIELDS },
    aborter.current.signal,
  );

  useEffect(() => {
    (result.error || subsidiaries.error) && dispatch(showErrorNotification(i18n, result.error || subsidiaries.error));
  }, [result.error, subsidiaries.error]);

  const categories = getCategories(i18n.t);
  const states = getStatus(i18n.t);
  const fuelTypes = getFuelTypes(i18n.t);
  const purchaseTypes = getPurchaseTypes(i18n.t);

  useEffect(() => {
    filters.setFilters("car.subsidiary.id", { operator: undefined, values: subsidiaryId ? [subsidiaryId] : [] });
  }, [subsidiaryId]);

  const filterOptions = {
    get: carsApi.getFieldFilters,
    update: filters.setFilters,
    getSelected: filters.getFilters,
  };

  const orderOptions = {
    by: filters.state.orderBy,
    desc: filters.state.desc,
    setBy: filters.setOrder,
  };

  const viewItems =
    subsidiaries.data?.items
      ?.filter((x) => !subsidiaryId || x.id !== subsidiaryId)
      .sort((x, y) => x.name.localeCompare(y.name, i18n.language))
      .map((x) => ({
        label: x.name,
        url: `/admin/subsidiaries/${x.id}/cars`,
      })) ?? [];

  const views: TableViews = {
    title: subsidiaries.data?.items?.find((x) => x.id === subsidiaryId)?.name ?? i18n.t.cars.all,
    items: subsidiaryId
      ? [
          {
            label: i18n.t.cars.all,
            url: `/admin/cars`,
          },
          ...viewItems,
        ]
      : viewItems,
    maxWidth: "200px",
  };

  const getRowActions = (item: CarSearch): Action[] => {
    const actions: Action[] = [];

    if (userSettings.permissions.cars.read) {
      actions.push({ title: i18n.t.common.view, onClick: () => navToEdit(item), icon: SvgArticle });
    }
    actions.push(getHistoryAction(i18n, () => setItemToShowVersions(item)));

    if (userSettings.permissions.cars.delete) {
      actions.push(getDeleteAction(i18n, () => setItemToDelete(item), true));
    }

    return actions;
  };

  const assTypes = getAssignmentTypes(i18n.t);

  return (
    <>
      <Header title={i18n.t.cars.plural} />
      <Panel>
        <CarStats query={query} />
        <Table<CarSearch>
          filters={filters}
          searchOptions={{
            onChange: () => {
              aborter.current.abort();
              aborter.current = new AbortController();
            },
          }}
          views={views}
          buttons={{
            main: !!userSettings.permissions.cars.insert && (
              <Button buttonProps={{ onClick: () => navigate("/admin/cars/new") }} glyph={SvgAdd} />
            ),
            secondary: (
              <ExportButton.Excel
                onClick={async () => {
                  const r = await carsApi.exportExcel(query, aborter.current.signal);
                  r && downloadFileFromData(r?.data, "cars", r?.contentType);
                }}
              />
            ),
          }}
          head={
            <>
              <Table.Th
                title={i18n.t.plateNumbers.singular}
                fieldName="car.lastPlateNumber"
                order={orderOptions}
                filters={filterOptions}
                filter={TextColumnFilter}
              />
              <Table.Th
                title={i18n.t.common.currentDriver}
                fieldName="car.driver.name"
                filters={filterOptions}
                filter={TextColumnFilter}
                formatter={(value) => assTypes.find((x) => x.value == value)?.label}
              />
              <Table.Th
                title={i18n.t.common.driverSince}
                fieldName="car.currenAssignment.startDate"
                order={orderOptions}
                filters={filterOptions}
                filter={DateColumnFilter}
                formatter={(value) =>
                  typeof value === "string" && value ? formatDateNumeric(value, i18n.language) : undefined
                }
              />
              <Table.Th
                title={i18n.t.common.costCenter}
                fieldName="car.currenAssignment.costCenter"
                order={orderOptions}
                filters={filterOptions}
                filter={TextColumnFilter}
              />
              <Table.Th
                title={i18n.t.common.category}
                fieldName="car.category"
                filters={filterOptions}
                filter={TextColumnFilter}
                order={orderOptions}
                formatter={(value) => getCategories(i18n.t).find((x) => x.value === value)?.label}
              />
              <Table.Th
                title={i18n.t.common.registrationDate}
                fieldName="car.registrationDate"
                filters={filterOptions}
                filter={DateColumnFilter}
                order={orderOptions}
                formatter={(value) =>
                  typeof value === "string" && value ? formatDateNumeric(value, i18n.language) : undefined
                }
              />
              <Table.Th
                title={i18n.t.manufacturers.singular}
                fieldName="car.manufacturer.name"
                filters={filterOptions}
                filter={TextColumnFilter}
                order={orderOptions}
              />
              <Table.Th
                title={i18n.t.common.model}
                fieldName="car.model"
                order={orderOptions}
                filters={filterOptions}
                filter={TextColumnFilter}
              />
              <Table.Th
                title={i18n.t.common.hp}
                fieldName="car.hp"
                order={orderOptions}
                filters={filterOptions}
                filter={NumberColumnFilter}
              />
              <Table.Th
                title={i18n.t.common.fuelType}
                fieldName="car.fuelType"
                filters={filterOptions}
                filter={TextColumnFilter}
                order={orderOptions}
                formatter={(value) => getFuelTypes(i18n.t).find((x) => x.value === value)?.label}
              />
              <Table.Th
                title={i18n.t.common.vehicleIdentificationNumber}
                fieldName="car.vehicleIdentificationNumber"
                order={orderOptions}
                filters={filterOptions}
                filter={TextColumnFilter}
              />
              <Table.Th
                title={i18n.t.common.lastMileage}
                fieldName="car.lastMileage"
                order={orderOptions}
                filters={filterOptions}
                filter={NumberColumnFilter}
              />

              <Table.Th
                title={i18n.t.common.privateCar}
                fieldName="car.isPrivateCar"
                filters={filterOptions}
                filter={BooleanColumnFilter}
              />
              <Table.Th
                title={i18n.t.common.state}
                fieldName="car.status"
                filters={filterOptions}
                filter={TextColumnFilter}
                order={orderOptions}
                formatter={(value) => getStatus(i18n.t).find((x) => x.value === value)?.label}
              />
              {!subsidiaryId && (
                <>
                  <Table.Th
                    title={i18n.t.subsidiaries.singular}
                    fieldName="car.subsidiary.name"
                    filters={filterOptions}
                    filter={TextColumnFilter}
                    order={orderOptions}
                  />
                  <Table.Th
                    title={i18n.t.common.semiramisOrganizationCode}
                    fieldName="car.subsidiary.semiramisOrganizationCode"
                    filters={filterOptions}
                    filter={TextColumnFilter}
                    order={orderOptions}
                  />
                </>
              )}
              <Table.Th
                title={i18n.t.common.otherAssignmentType}
                fieldName="car.currenAssignment.type"
                filters={filterOptions}
                filter={TextColumnFilter}
                order={orderOptions}
                formatter={(v) => assTypes.find((x) => x.value == v)?.label ?? i18n.t.common.empty}
              />
              <Table.Th
                title={i18n.t.common.purchaseType}
                fieldName="car.purchaseType"
                filters={filterOptions}
                filter={TextColumnFilter}
                order={orderOptions}
                formatter={(value) =>
                  getPurchaseTypes(i18n.t).find((x) => x.value === value)?.label ?? i18n.t.common.empty
                }
              />

              <th></th>
            </>
          }
          onRowClick={(item) => {
            navigate(`/admin/cars/${item.id}`);
          }}
          renderRow={(item) => (
            <>
              <td>
                {isWithinLast48h(item.createdAt) && <NewItemIcon />}
                <CustomLink to={`/admin/cars/${item.id}`}>{item.currentPlateNumber || item.lastPlateNumber}</CustomLink>
              </td>
              <td>
                {item.currentAssignment.carDriver ? (
                  <CustomLink
                    to={""}
                    onClick={(e) => {
                      e.preventDefault();
                      setCarDriverIdToEdit(item.currentAssignment.carDriver?.id);
                    }}
                  >
                    {item.currentAssignment.carDriver?.driver?.fullName}
                  </CustomLink>
                ) : (
                  item.currentAssignment.otherCarAssignment && (
                    <CustomLink
                      to={""}
                      onClick={(e) => {
                        e.preventDefault();
                        setOtherAssignmentIdToEdit(item.currentAssignment.otherCarAssignment.id);
                      }}
                    >
                      {assTypes.find((x) => x.value == item.currentAssignment.otherCarAssignment?.type)?.label}
                    </CustomLink>
                  )
                )}
              </td>
              <td>
                {item.currentAssignment.carDriver ? (
                  <>{formatDateNumeric(item.currentAssignment.carDriver?.startDate, i18n.language)}</>
                ) : (
                  item.currentAssignment.otherCarAssignment && (
                    <>{formatDateNumeric(item.currentAssignment.otherCarAssignment.startDate, i18n.language)}</>
                  )
                )}
              </td>

              <td>
                {item.currentAssignment.carDriver ? (
                  <>{item.currentAssignment.carDriver?.driver?.currentCostCenter}</>
                ) : (
                  item.currentAssignment.otherCarAssignment && (
                    <>{item.currentAssignment.otherCarAssignment.costCenter}</>
                  )
                )}
              </td>
              <td>{categories.find((x) => x.value == item.category)?.label}</td>
              <td>{item.registrationDate && formatDateNumeric(item.registrationDate, i18n.language)}</td>
              <td>
                <CustomLink
                  to={""}
                  onClick={(e) => {
                    e.preventDefault();
                    setManufacturerIdToEdit(item.manufacturer.id);
                  }}
                >
                  {item.manufacturer.name}
                </CustomLink>
              </td>
              <td>{item.model}</td>
              <td>{item.hp}</td>
              <td>{fuelTypes.find((x) => x.value == item.fuelType)?.label}</td>
              <td>{item.vehicleIdentificationNumber}</td>
              <td>
                {item.lastMileage != null
                  ? formatNumber(item.lastMileage, i18n.language, {
                      fractionalDigits: 0,
                      showThousendSeparator: true,
                    })
                  : undefined}
              </td>
              <td>
                <Check
                  icon={item.isPrivateCar ? SvgCheckCircle : SvgCancel}
                  color={item.isPrivateCar ? "green" : "red"}
                  obscured={!item.isPrivateCar}
                />
              </td>
              <td>
                <State
                  text={states.find((x) => x.value == item.status)?.label ?? ""}
                  color={getCarStateColor(item.status)}
                />
              </td>
              {!subsidiaryId && (
                <>
                  <td>
                    {item.carSubsidiary && (
                      <CustomLink to={`/admin/subsidiaries/${item.carSubsidiary.subsidiary.id}`}>
                        {item.carSubsidiary.subsidiary.name}
                      </CustomLink>
                    )}
                  </td>
                  <td>{item.carSubsidiary?.subsidiary.semiramisOrganizationCode}</td>
                </>
              )}
              <td>
                {!!item.currentAssignment.otherCarAssignment && (
                  <>{assTypes.find((x) => x.value == item.currentAssignment.otherCarAssignment?.type)?.label}</>
                )}
              </td>
              <td>{purchaseTypes.find((x) => x.value == item.purchaseType)?.label}</td>
            </>
          )}
          rowActions={getRowActions}
          error={result.error}
          items={cars?.items}
          total={cars?.total}
        />
      </Panel>
      {!!itemToDelete && <DeleteCarDialog carId={itemToDelete.id} onClose={() => setItemToDelete(undefined)} />}

      {!!itemToShowVersions && (
        <HistoryDialog
          title={i18n.t.cars.versions}
          entityId={itemToShowVersions.id}
          lastVersion={itemToShowVersions.versionNumber}
          getHistory={carsApi.getCarHistory}
          getEntityVersionHook={carsApi.getCarVersion}
          onClose={() => setItemToShowVersions(undefined)}
          renderForm={(headers, dataVersion) => (
            <>
              <VersionDialog.Table headers={headers}>
                {dataVersion.current?.vehicleIdentificationNumber != dataVersion.next.vehicleIdentificationNumber && (
                  <tr>
                    <VersionDialog.Field
                      label={i18n.t.common.vehicleIdentificationNumber}
                      value={dataVersion.current?.vehicleIdentificationNumber}
                      edited={dataVersion.next.vehicleIdentificationNumber}
                    />
                  </tr>
                )}
                {(dataVersion.current?.manufacturer?.id ?? null) !== (dataVersion.next.manufacturer?.id ?? null) && (
                  <tr>
                    <VersionDialog.Field
                      label={i18n.t.manufacturers.singular}
                      value={dataVersion.current?.manufacturer?.name}
                      edited={dataVersion.next.manufacturer?.name}
                    />
                  </tr>
                )}
                {dataVersion.current?.model != dataVersion.next.model && (
                  <tr>
                    <VersionDialog.Field
                      label={i18n.t.common.model}
                      value={dataVersion.current?.model}
                      edited={dataVersion.next.model}
                    />
                  </tr>
                )}
                {dataVersion.current?.hp != dataVersion.next.hp && (
                  <tr>
                    <VersionDialog.Field
                      label={i18n.t.common.hp}
                      value={dataVersion.current?.hp}
                      edited={dataVersion.next.hp}
                    />
                  </tr>
                )}
                {dataVersion.current?.fuelType != dataVersion.next.fuelType && (
                  <tr>
                    <VersionDialog.Field
                      label={i18n.t.common.fuelType}
                      value={fuelTypes.find((x) => x.value == dataVersion.current?.fuelType)?.label}
                      edited={fuelTypes.find((x) => x.value == dataVersion.next.fuelType)?.label}
                    />
                  </tr>
                )}
                {dataVersion.current?.category != dataVersion.next.category && (
                  <tr>
                    <VersionDialog.Field
                      label={i18n.t.common.category}
                      value={categories.find((x) => x.value == dataVersion.current?.category)?.label}
                      edited={categories.find((x) => x.value == dataVersion.next.category)?.label}
                    />
                  </tr>
                )}
                {dataVersion.current?.isPrivateCar != dataVersion.next.isPrivateCar && (
                  <tr>
                    <VersionDialog.Field
                      label={i18n.t.common.privateCar}
                      value={formatBoolean(i18n.t, dataVersion.current.isPrivateCar)}
                      edited={formatBoolean(i18n.t, dataVersion.next.isPrivateCar)}
                    />
                  </tr>
                )}
                {dataVersion.current?.status != dataVersion.next.status && (
                  <tr>
                    <VersionDialog.Field
                      label={i18n.t.common.state}
                      value={states.find((x) => x.value == dataVersion.current?.status)?.label}
                      edited={states.find((x) => x.value == dataVersion.next.status)?.label}
                    />
                  </tr>
                )}
                {dataVersion.current?.registrationDate != dataVersion.next.registrationDate && (
                  <tr>
                    <VersionDialog.Field
                      label={i18n.t.common.registrationDate}
                      value={dataVersion.current?.registrationDate}
                      edited={dataVersion.next.registrationDate}
                    />
                  </tr>
                )}
                {dataVersion.current?.purchaseDate != dataVersion.next.purchaseDate && (
                  <tr>
                    <VersionDialog.Field
                      label={i18n.t.common.purchaseDate}
                      value={dataVersion.current?.purchaseDate}
                      edited={dataVersion.next.purchaseDate}
                    />
                  </tr>
                )}
                {dataVersion.current?.soldDate != dataVersion.next.soldDate && (
                  <tr>
                    <VersionDialog.Field
                      label={i18n.t.common.soldDate}
                      value={dataVersion.current?.soldDate}
                      edited={dataVersion.next.soldDate}
                    />
                  </tr>
                )}
                {(!dataVersion.current || dataVersion.current?.notes != dataVersion.next.notes) && (
                  <tr>
                    <VersionDialog.Field
                      label={i18n.t.common.notes}
                      value={
                        dataVersion.current && (
                          <Textarea
                            value={dataVersion.current && !dataVersion.current?.notes ? "" : dataVersion.current?.notes}
                            disabled
                          />
                        )
                      }
                      edited={<Textarea value={dataVersion.next.notes} disabled />}
                    />
                  </tr>
                )}
              </VersionDialog.Table>
            </>
          )}
        />
      )}

      {!!carDriverIdToEdit && (
        <EditCarDriverDialog carDriverId={carDriverIdToEdit} onClose={() => setCarDriverIdToEdit(undefined)} />
      )}

      {!!otherAssignmentIdToEdit && (
        <EditOtherCarAssignmentDialog
          otherCarAssignmentId={otherAssignmentIdToEdit}
          onClose={() => setOtherAssignmentIdToEdit(undefined)}
        />
      )}

      {!!manufacturerIdToEdit && (
        <EditManufacturerDialog
          manufacturerId={manufacturerIdToEdit}
          onClose={() => setManufacturerIdToEdit(undefined)}
        />
      )}
    </>
  );
};

export default Cars;
